"use client"; // Ensure this is at the top

import type { AnimationItem } from "lottie-web";
import dynamic from "next/dynamic";
import { type JSX, useEffect, useRef, useState } from "react";

const Player = dynamic(() => import("@lottiefiles/react-lottie-player").then((mod) => mod.Player), {
    ssr: false,
});

type LottieVisibleProps = {
    src: string;
    className?: string;
    loop?: boolean;
};

/**
 * A special Lottie version of the Lottie player, that only plays while the component is visible.
 */
export function LottieVisible({ src, className, loop, ...rest }: LottieVisibleProps): JSX.Element {
    const [lottie, setLottie] = useState<AnimationItem | null>(null);
    const lottieRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting === true) {
                lottie?.play();
            } else {
                lottie?.stop();
            }
        });

        const currentElement = lottieRef.current;

        if (currentElement !== null) {
            observer.observe(currentElement);
        }

        return () => {
            if (currentElement !== null) {
                observer.unobserve(currentElement);
            }
        };
    }, [lottie]);

    return (
        <div ref={lottieRef} className={className}>
            <Player
                src={src}
                lottieRef={(lottie) => {
                    setLottie(lottie);
                }}
                {...rest}
                loop={loop}
                keepLastFrame
            />
        </div>
    );
}
